import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    UpdateLeadSummary as UpdateLeadSummaryMutation,
    UpdateLeadSummaryVariables as UpdateLeadSummaryVariables,
    UpdateLeadInputFieldInput,
    LeadSummaryFragment as LeadSummaryFragment,
} from 'nutshell-graphql-types';

import LEAD_SUMMARY_FRAGMENT from '../fragments/lead-summary-fragment.graphql';
import UPDATE_LEAD_SUMMARY from '../mutations/update-lead-summary.graphql';

export const useUpdateLeadSummary = (): {
    updateLead: (
        input: UpdateLeadInputFieldInput,
        leadData?: LeadSummaryFragment | null | undefined
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [updateLeadCustomFieldValue, {loading, error}] = useMutation<
        UpdateLeadSummaryMutation,
        UpdateLeadSummaryVariables
    >(UPDATE_LEAD_SUMMARY);

    const updateLead = React.useCallback(
        (input: UpdateLeadInputFieldInput, leadData?: LeadSummaryFragment | null) => {
            // If no leadData is provided, use simple mutation without optimistic response
            if (!leadData) {
                return updateLeadCustomFieldValue({variables: {lead: input}});
            }

            let newLeadDueDate;
            if (input.dueTime === undefined) {
                newLeadDueDate = leadData.anticipatedClosedTime;
            } else if (input.dueTime === null) {
                newLeadDueDate = null;
            } else {
                newLeadDueDate = input.dueTime;
            }

            // Prepare the input for mutation
            const mutationInput = {...input} as const;

            return updateLeadCustomFieldValue({
                variables: {lead: mutationInput},
                optimisticResponse: {
                    updateLead: {
                        __typename: 'UpdateLeadPayload',
                        lead: {
                            ...leadData,
                            ...input,
                            anticipatedClosedTime: newLeadDueDate,
                            confidence: input.confidence ?? leadData.confidence,
                        },
                    },
                },
                update: (proxy, mutationResults) => {
                    const updatedLead =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.updateLead &&
                        mutationResults.data.updateLead.lead;

                    if (!updatedLead) return;

                    proxy.writeFragment({
                        id: input.id,
                        fragment: LEAD_SUMMARY_FRAGMENT,
                        fragmentName: 'LeadSummaryFragment',
                        data: {
                            ...updatedLead,
                        },
                    });
                },
            });
        },
        [updateLeadCustomFieldValue]
    );

    return {
        updateLead,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
